import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { get } from "lodash";
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { amountToShow } from "global/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateACHSubscription } from "store/slice/payment/paymentSlice";

const ACHPaymentForm = ({ clientSecret, subscriptionData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [isLoading, setIsLoading] = useState(false);
  const [intentId, setIntentId] = useState("");
  const [alert, setAlert] = useState({ isAlert: false });

  const customer = get(subscriptionData, "customerId", {});

  const customerName = `${get(customer, "firstName", "")} ${get(
    customer,
    "lastName",
    "",
  )}`.trim();

  const customerEmail = get(customer, "email", "");
  const customerPhone = get(customer, "secondaryMobile", "");

  const totalPayAmount = subscriptionData.isMonthly
    ? get(subscriptionData, "firstMonthTotalAmount", 0)
    : get(subscriptionData, "totalAmount", 0);

  const subscriptionContract = get(
    subscriptionData,
    "placeId.subscriptionContract",
    "",
  );

  useEffect(() => {
    if (!stripe || !clientSecret) return;

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        switch (paymentIntent?.status) {
          case "succeeded":
          case "processing":
            navigate(
              `/payment/payment-result-ach?SN=${
                subscriptionData.subscriptionNumber || ""
              }&total=${totalPayAmount}`,
            );
            break;

          case "requires_payment_method":
            setIntentId(paymentIntent.id);
            break;

          default:
            setAlert({
              isAlert: true,
              error: true,
              message: "Something went wrong.",
            });
        }
      })
      .catch((err) => {
        console.error("Error retrieving payment intent:", err);

        setAlert({
          isAlert: true,
          error: true,
          message: "Failed to retrieve payment intent.",
        });
      });
  }, [clientSecret, stripe, navigate, subscriptionData, totalPayAmount]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setAlert({ isAlert: false });

    try {
      // Get billing address from Stripe AddressElement
      const addressElement = elements.getElement(AddressElement);

      if (!addressElement) {
        setAlert({
          isAlert: true,
          error: true,
          message: "Address information is unavailable.",
        });

        setIsLoading(false);
        return;
      }

      const { complete, value } = await addressElement.getValue();

      // Validate address
      if (!complete) {
        setAlert({
          isAlert: true,
          error: true,
          message: "Please complete your billing address.",
        });

        setIsLoading(false);
        return;
      }

      const billingAddress = value.address;

      // Update ACH subscription with address
      const result = await dispatch(
        updateACHSubscription({
          subscriptionId: subscriptionData._id,
          subscriptionContract,
          intentId,
          address: billingAddress,
        }),
      ).unwrap();

      // Confirm ACH payment
      const response = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${process.env.REACT_APP_FRONT_END_DOMAIN}/payment/payment-result-ach?SN=${result.subscriptionNumber}&total=${totalPayAmount}`,
          receipt_email: customerEmail,

          // Pass customer billing information to Stripe
          payment_method_data: {
            billing_details: {
              name: customerName,
              email: customerEmail,
              phone: customerPhone,
              address: billingAddress,
            },
          },
        },
      });

      if (response.error) {
        setAlert({
          isAlert: true,
          error: true,
          message: response.error.message || "An unexpected error occurred.",
        });
      } else {
        setAlert({
          isAlert: true,
          error: false,
          message: "Payment succeeded!",
        });
      }
    } catch (err) {
      console.error("Payment error:", err);

      setAlert({
        isAlert: true,
        error: true,
        message:
          err?.message || "An error occurred during the payment process.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {alert.isAlert && (
        <Alert
          severity={alert.error ? "error" : "success"}
          onClose={() => setAlert({ isAlert: false })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Billing Address */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
        }}
      >
        <AddressElement
          options={{
            mode: "billing",
            autocomplete: {
              mode: "automatic",
            },
            fields: {
              phone: customerPhone ? "never" : "always",
            },
            validation: customerPhone
              ? undefined
              : {
                  phone: {
                    required: "always",
                  },
                },
            defaultValues: {
              name: customerName,
              address: {
                country: "US",
              },
            },
          }}
        />
      </Paper>

      {/* ACH Payment Details */}
      <Paper
        variant="outlined"
        className="p-2"
        sx={{
          py: 1.3,
          width: "100%",
        }}
      >
        <PaymentElement
          id="payment-element"
          options={{
            defaultValues: {
              billingDetails: {
                name: customerName,
                email: customerEmail,
                phone: customerPhone,
              },
            },
          }}
        />
      </Paper>

      <Box mt={2} className="text-center">
        <MDButton
          variant="contained"
          color={sidenavColor}
          onClick={handleSubmit}
          disabled={isLoading}
          sx={{
            width: {
              xs: "150px",
              sm: "250px",
            },
          }}
        >
          {`Pay $${amountToShow(totalPayAmount)}`}
        </MDButton>
      </Box>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <Stack direction="column" spacing={1} alignItems="center">
          <CircularProgress color="inherit" />

          <Typography
            variant="h5"
            sx={{
              color: "primary.contrastText",
            }}
          >
            Please wait...
          </Typography>
        </Stack>
      </Backdrop>
    </>
  );
};

export default ACHPaymentForm;
